// React components
import { memo, useState, useEffect } from "react";

// MUI Components
import { Grid, TextField, FormControl, IconButton, Tooltip} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const FileRow = memo(function FileRow({
    rowIndex,
    fields,
    values,
    onChange,
    onRemove,
    error
}) {
    const [localValues, setLocalValues] = useState(values || {});
    const [fileAdded, setFileAdded] = useState(false);
    const [localErrors, setLocalErrors] = useState({});

    // Syncs localValues with parent component values in case of external updates (e.g. delete row)
    useEffect(() => {
        setLocalValues(values || {});
        setLocalErrors({});
        if (values.file || values?.fileAdded) {
            setFileAdded(true);
        } else {
            setFileAdded(false);
        }
    }, [values]);

    // Handles changes in input fields
    const handleChange = (name, value, type) => {
        const newLocalValues = { ...localValues, [name]: value };
        if (type === "date") { handleDateBlur(newLocalValues); }

        if (name === "file" && value) {
            const isValid = processAddFile(newLocalValues);

            if (isValid) {
                setFileAdded(true);
                setLocalValues(newLocalValues);
                onChange(rowIndex, newLocalValues);
            } else {
                setFileAdded(false);
            }

        } else if (name === "file" && value === null) {
            setLocalValues({...newLocalValues, toDelete: true});
            onChange(rowIndex, {...newLocalValues, toDelete: true, fileAdded: false});
        } else {
            setLocalValues(newLocalValues);
        }
    };

    // Handles field blur event to trigger parent update
    const handleFieldBlur = () => {
        onChange(rowIndex, localValues);
    }

    // handles date field blur event to trigger parent update
    const handleDateBlur = (currentLocalValues) => {
        onChange(rowIndex, currentLocalValues);
    }

    // Clears the selected file
    const handleClearFile = () => {
        handleChange("file", null);
        setFileAdded(false);
    }

    // Processes adding a file and validates requires fields
    const processAddFile = (newLocalValues) => {
        const errors = validateFields();

        if (Object.keys(errors).length > 0) {
            setLocalErrors(errors);
            return false;
        } else {
            setLocalErrors({});
            return true;
        }

    }

    // Validates required fields before adding a file
    const validateFields = () => {
        const errors = {};
        fields.forEach((field) => {
            if (!localValues[field.name]) {
                errors[field.name] = `${field.label} is required`;
            }
        }); 
        return errors;
    }

    return (
        <MDBox
            sx={{
                border: fileAdded ? "1px solid green" : "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "4px",
            }}
        >
            <Grid container spacing={1}>

                {/* Render a FileRow for each entry in filesData */}
                {fields.map((field, index) => (
                    <Grid item xs={12} sm={12 / (fields.length + 1)} key={index}>
                        <MDBox
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            {/* FieldType Switch */}
                            {field.type === "date" ? (
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label={field.label}
                                        value={localValues[field.name] || null}
                                        onChange={(val) => handleChange(field.name, val, "date")}
                                        errors={localErrors[field.name]}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: "small",
                                                error: localErrors[field.name] || error[field.name],
                                                helperText: localErrors[field.name] || error[field.name],
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            ) : (
                                <FormControl fullWidth variant="outlined">
                                    <TextField
                                        fullWidth
                                        label={field.label}
                                        name={field.name}
                                        type={field.type || "text"}
                                        size="small"
                                        value={localValues[field.name] || ""}
                                        onChange={(e) => handleChange(field.name, e.target.value, field.type)}
                                        onBlur={handleFieldBlur}
                                        error={localErrors[field.name] || error[field.name]}
                                        helperText={localErrors[field.name] || error[field.name]}
                                    />
                                </FormControl>
                            )}
                        </MDBox>
                    </Grid>
                ))}

                <Grid item xs={12} sm={12 / (fields.length + 1)}>
                    <MDBox
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >

                        {/* Add File Button */}
                        {!fileAdded && (
                            <MDBox display="flex" flexDirection="column" alignItems="center" width="100%">
                                <MDButton
                                    variant="outlined"
                                    color="primary"
                                    component="label"
                                    fullWidth
                                    height="45px"
                                    size="small"
                                >
                                    Add File
                                    <input
                                        type="file"
                                        hidden
                                        onChange={(e) =>
                                            handleChange("file", e.target.files?.[0])
                                        }
                                    />
                                </MDButton>
                                {error.file && (
                                    <MDBox width="100%">
                                        <MDTypography variant="caption" color="error">{error.file}</MDTypography>
                                    </MDBox>
                                )}
                            </MDBox>
                        )}
                        {/* Clear File Button */}
                        {fileAdded && (
                            <MDButton
                                variant="outlined"
                                color="success"
                                component="label"
                                fullWidth
                                height="45px"
                                size="small"
                                onClick={handleClearFile}
                            >
                                Clear
                            </MDButton>
                        )}

                        {/* File Action Buttons */}
                        <MDBox>
                            <Grid container>

                                {/* Delete FileRow Button */}
                                <Grid item xs={12}>
                                    <Tooltip title="Remove Row">
                                        <IconButton onClick={() => onRemove(rowIndex)}>
                                            <CloseIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>

                            </Grid>
                        </MDBox>
                    </MDBox>
                </Grid>

            </Grid>
        </MDBox>
    );
});
export default FileRow;